import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  RefreshCw,
  Save,
  ArrowLeft,
  ExternalLink,
  Image
} from 'lucide-react';

const categories = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'retail', label: 'Retail' },
  { value: 'service', label: 'Service' },
  { value: 'school', label: 'School' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'other', label: 'Other' }
];

const MyBusinessPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    hours: '',
    services: '',
    social_facebook: '',
    social_instagram: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchBusinesses();
  }, [user, navigate]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/my-businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (business = null) => {
    if (business) {
      setEditingBusiness(business);
      setFormData({
        name: business.name,
        category: business.category,
        description: business.description,
        address: business.address,
        phone: business.phone || '',
        email: business.email || '',
        website: business.website || '',
        hours: business.hours || '',
        services: business.services?.join(', ') || '',
        social_facebook: business.social_facebook || '',
        social_instagram: business.social_instagram || ''
      });
    } else {
      setEditingBusiness(null);
      setFormData({
        name: '',
        category: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        hours: '',
        services: '',
        social_facebook: '',
        social_instagram: ''
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category || !formData.description || !formData.address) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        services: formData.services ? formData.services.split(',').map(s => s.trim()) : []
      };

      if (editingBusiness) {
        await axios.put(`${API}/businesses/${editingBusiness.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Business updated successfully');
      } else {
        await axios.post(`${API}/businesses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Business submitted for review');
      }

      setShowForm(false);
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save business');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  if (showForm) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Button variant="ghost" onClick={() => setShowForm(false)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-500" />
                {editingBusiness ? 'Edit Business' : 'Add Your Business'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name">Business Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder="Your business name"
                      required
                      data-testid="business-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}
                    >
                      <SelectTrigger data-testid="business-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="242-XXX-XXXX"
                      data-testid="business-phone-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                      placeholder="Full business address"
                      required
                      data-testid="business-address-input"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                      placeholder="Tell customers about your business..."
                      rows={4}
                      required
                      data-testid="business-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      placeholder="business@email.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="hours">Business Hours</Label>
                    <Input
                      id="hours"
                      value={formData.hours}
                      onChange={(e) => setFormData(p => ({ ...p, hours: e.target.value }))}
                      placeholder="Mon-Fri 9am-5pm, Sat 10am-2pm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="services">Services (comma-separated)</Label>
                    <Input
                      id="services"
                      value={formData.services}
                      onChange={(e) => setFormData(p => ({ ...p, services: e.target.value }))}
                      placeholder="Delivery, Pickup, Catering"
                    />
                  </div>

                  <div>
                    <Label htmlFor="facebook">Facebook Page URL</Label>
                    <Input
                      id="facebook"
                      value={formData.social_facebook}
                      onChange={(e) => setFormData(p => ({ ...p, social_facebook: e.target.value }))}
                      placeholder="https://facebook.com/..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="instagram">Instagram URL</Label>
                    <Input
                      id="instagram"
                      value={formData.social_instagram}
                      onChange={(e) => setFormData(p => ({ ...p, social_instagram: e.target.value }))}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-business-btn"
                >
                  {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingBusiness ? 'Update Business' : 'Submit Business'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-emerald-500" /> My Businesses
            </h1>
            <p className="text-slate-600 mt-1">Manage your business listings</p>
          </div>
          <Button onClick={() => handleOpenForm()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-business-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Business
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : businesses.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg text-slate-900">No Businesses Yet</h3>
              <p className="text-slate-500 mt-2 mb-4">Add your first business to the Killarney directory!</p>
              <Button onClick={() => handleOpenForm()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> Add Your Business
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {businesses.map((business) => (
              <Card key={business.id} className="bg-white" data-testid={`my-business-${business.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="w-20 h-20 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {business.logo_url ? (
                        <img src={business.logo_url} alt={business.name} className="w-16 h-16 object-contain" />
                      ) : (
                        <Building2 className="w-8 h-8 text-emerald-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-heading font-bold text-lg text-slate-900">{business.name}</h3>
                          <p className="text-sm text-emerald-600 capitalize">{business.category}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleOpenForm(business)} data-testid={`edit-business-${business.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Link to={`/directory/${business.id}`}>
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                      <p className="text-slate-600 mt-2 line-clamp-2">{business.description}</p>
                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {business.address}</span>
                        {business.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> {business.phone}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Link to="/directory" className="block mt-6">
          <Button variant="outline" className="w-full">
            <Building2 className="w-4 h-4 mr-2" /> View Full Directory
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default MyBusinessPage;
